import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiUpdateMyProvince } from "@/lib/api";
import { CANADIAN_PROVINCES } from "@/constants/provinces";

/**
 * Global, non-dismissable modal that forces any logged-in user without a
 * `province` value on their profile to pick one before they can use the app.
 * Catches legacy buyers created before the province field existed.
 */
export default function ProvinceGate() {
  const { user, refresh } = useAuth();
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const needsProvince = !!user && !user.province;

  useEffect(() => {
    if (!needsProvince) setValue("");
  }, [needsProvince]);

  if (!needsProvince) return null;

  const onSave = async (e) => {
    e.preventDefault();
    if (!value) return;
    setSaving(true);
    try {
      await apiUpdateMyProvince(value);
      await refresh();
      toast.success("Province saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save province");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="province-gate-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="province-gate-title"
    >
      <form
        onSubmit={onSave}
        data-testid="province-gate-modal"
        className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-stone-200"
      >
        <div className="px-6 pt-6 pb-2">
          <h2
            id="province-gate-title"
            className="text-xl font-semibold text-stone-900"
            data-testid="province-gate-title"
          >
            One quick thing — confirm your province
          </h2>
          <p className="mt-2 text-sm text-stone-600">
            We need your province to apply the correct sales tax (GST/PST) on your
            invoices. This only takes a second.
          </p>
        </div>
        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-stone-700 mb-1">
            Province
          </label>
          <select
            data-testid="province-gate-select"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            required
            autoFocus
            className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10"
          >
            <option value="" disabled>Select province</option>
            {CANADIAN_PROVINCES.map((p) => (
              <option key={p.code} value={p.code}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="px-6 pb-6 pt-2 flex items-center justify-end">
          <button
            type="submit"
            data-testid="province-gate-submit"
            disabled={!value || saving}
            className="rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save & continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
